import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, Plus } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { INTEGRATION_API } from "../../api/auth";
import { youcanWebhooksDelete } from "../../api/auth";
import { EmptyState, FeedbackMessage, getApiErrorMessage, LoadingState } from "../ui/Feedback";

const Integration_list = () => {
  const [data, setData] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const fetchIntegrations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await INTEGRATION_API.get("integrations/stores/");
      setData(res.data || []);
    } catch (error) {
      setAlert({
        type: "error",
        message: getApiErrorMessage(error, "Impossible de charger les integrations."),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const message = params.get("message");
    const platform = params.get("platform");

    if (!status) return;

    if (status === "success") {
      setAlert({
        type: "success",
        message: `${platform} connected successfully`,
      });
      fetchIntegrations();
    }

    if (status === "error") {
      setAlert({
        type: "error",
        message: message || "Integration failed.",
      });
    }

    navigate("/integration", { replace: true });
  }, [location.search, navigate, fetchIntegrations]);

  const handleActivate = async (integration) => {
    try {
      const platform = integration.platform.toLowerCase();

      if (platform === "youcan") {
        await INTEGRATION_API.post(
          `wbhooks/youcan/${integration.id}/webhooks/`,
          { event: "order.create" }
        );
      }

      if (platform === "shopify") {
        await INTEGRATION_API.post(
          `wbhooks/shopify/${integration.id}/webhooks/`,
          { topic: "orders/create" }
        );
      }

      setAlert({
        type: "success",
        message: `${integration.platform} activated successfully`,
      });

      fetchIntegrations();
    } catch (error) {
      setAlert({
        type: "error",
        message: getApiErrorMessage(error, "Activation failed."),
      });
    }
  };

  const handleDelete = async () => {
    try {
      const integration = data.find((i) => i.id === deleteId);
      if (!integration) return;

      const platform = integration.platform.toLowerCase();

      if (platform === "youcan") {
        await youcanWebhooksDelete(deleteId, "order.create");
      }

      if (platform === "shopify") {
        await INTEGRATION_API.delete(
          `wbhooks/shopify/${deleteId}/webhooks/`,
          {
            data: { topic: "orders/create" },
          }
        );
      }

      setDeleteId(null);

      setAlert({
        type: "success",
        message: `${integration.platform} webhook deleted successfully`,
      });

      fetchIntegrations();
    } catch (error) {
      setAlert({
        type: "error",
        message: getApiErrorMessage(error, "Failed to delete webhook."),
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-4 sm:px-6 sm:py-6 dark:bg-slate-950">
      <div className="mx-auto max-w-7xl">
        <AnimatePresence>
          {alert && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-5"
            >
              <FeedbackMessage
                type={alert.type}
                title={alert.type === "success" ? "Operation reussie" : "Action impossible"}
                message={alert.message}
                onClose={() => setAlert(null)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800 sm:text-3xl dark:text-slate-100">
              Installed Stores
            </h1>
            <p className="mt-1 text-sm text-gray-500 sm:text-base dark:text-slate-500">
              Manage connected stores integrations.
            </p>
          </div>

          <button
            onClick={() => navigate("/integrations/add")}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-white shadow transition hover:bg-indigo-700 sm:w-auto"
          >
            <Plus size={18} />
            Add Store
          </button>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          {loading ? (
            <div className="p-4"><LoadingState label="Loading stores..." /></div>
          ) : data.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title="Aucune boutique connectee"
                message="Connectez Shopify ou YouCan pour synchroniser automatiquement les commandes, importer les produits et preparer les collaborations marketplace."
                action={
                  <button
                    onClick={() => navigate("/integrations/add")}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    <Plus size={16} />
                    Add Store
                  </button>
                }
              />
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="border-b bg-gray-50 dark:bg-slate-800 dark:border-slate-800">
                    <tr>
                      <th className="px-6 py-4 text-left dark:text-slate-300">Store Name</th>
                      <th className="px-6 py-4 text-left dark:text-slate-300">Platform</th>
                      <th className="px-6 py-4 text-left dark:text-slate-300">Status</th>
                      <th className="px-6 py-4 text-right dark:text-slate-300">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {data.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50 dark:border-slate-800 dark:hover:bg-slate-800">
                        <td className="px-6 py-4 dark:text-slate-300">{item.store_name}</td>
                        <td className="px-6 py-4 capitalize dark:text-slate-300">{item.platform}</td>
                        <td className="px-6 py-4">
                          <span
                            className={`rounded-full px-3 py-1 text-xs ${
                              item.is_active
                                ? "bg-green-100 text-green-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                                : "bg-red-100 text-red-600 dark:bg-rose-500/10 dark:text-rose-400"
                            }`}
                          >
                            {item.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>

                        <td className="space-x-3 px-6 py-4 text-right">
                          {!item.is_active && (
                            <button
                              onClick={() => handleActivate(item)}
                              className="rounded-lg bg-green-600 px-3 py-1 text-xs text-white transition hover:bg-green-700"
                            >
                              Activate
                            </button>
                          )}

                          {item.is_active && (
                            <button
                              onClick={() => setDeleteId(item.id)}
                              className="text-red-500 hover:text-red-700 dark:text-rose-400 dark:hover:text-rose-300"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="space-y-3 p-3 md:hidden">
                {data.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl border border-gray-100 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="truncate text-base font-semibold text-gray-900 dark:text-slate-100">
                          {item.store_name}
                        </h3>
                        <p className="mt-1 text-sm capitalize text-gray-500 dark:text-slate-500">
                          {item.platform}
                        </p>
                      </div>

                      <span
                        className={`shrink-0 rounded-full px-3 py-1 text-xs ${
                          item.is_active
                            ? "bg-green-100 text-green-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                            : "bg-red-100 text-red-600 dark:bg-rose-500/10 dark:text-rose-400"
                        }`}
                      >
                        {item.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="mt-4 flex gap-2">
                      {!item.is_active && (
                        <button
                          onClick={() => handleActivate(item)}
                          className="flex-1 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-green-700"
                        >
                          Activate
                        </button>
                      )}

                      {item.is_active && (
                        <button
                          onClick={() => setDeleteId(item.id)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-red-200 text-red-500 transition hover:bg-red-50 dark:border-rose-500/30 dark:text-rose-400 dark:hover:bg-rose-500/10"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>

        <AnimatePresence>
          {deleteId && (
            <motion.div
              className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl sm:p-8 dark:bg-slate-900"
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
              >
                <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-slate-100">
                  Delete Store?
                </h3>

                <p className="mb-5 text-sm text-gray-500 dark:text-slate-500">
                  This action will remove the webhook connection for this store.
                </p>

                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    onClick={() => setDeleteId(null)}
                    className="w-full rounded-lg bg-gray-100 px-4 py-2.5 sm:w-auto dark:bg-slate-800 dark:text-slate-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="w-full rounded-lg bg-red-600 px-4 py-2.5 text-white sm:w-auto"
                  >
                    Delete
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Integration_list;
